import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Mail } from "lucide-react";
import { KENTRO } from "@/lib/constants";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade — Kentro Sistemas" },
      {
        name: "description",
        content:
          "Política de Privacidade da Plataforma Kentro: como coletamos, usamos, compartilhamos e protegemos seus dados pessoais conforme a LGPD.",
      },
      { property: "og:title", content: "Política de Privacidade — Kentro Sistemas" },
      {
        property: "og:description",
        content:
          "Como a Kentro Sistemas trata dados pessoais na Plataforma de Gestão de Atendimento Omnichannel.",
      },
      { property: "og:url", content: "https://kentro.com.br/privacidade" },
    ],
    links: [{ rel: "canonical", href: "https://kentro.com.br/privacidade" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Política de Privacidade — Kentro Sistemas",
          url: "https://kentro.com.br/privacidade",
          inLanguage: "pt-BR",
          dateModified: "2026-06-24",
          publisher: {
            "@type": "Organization",
            name: "Barbalho Solutions Ltda.",
          },
        }),
      },
    ],
  }),
  component: PrivacidadePage,
});

function PrivacidadePage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5" /> LGPD · Lei 13.709/2018
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            Política de <span className="gradient-text">Privacidade</span>
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Última atualização: 24 de junho de 2026.
          </p>
          <p className="mt-5 max-w-2xl text-base text-muted-foreground">
            A <strong className="text-foreground">Barbalho Solutions Ltda.</strong>{" "}
            (“Kentro”, “nós”), inscrita no CNPJ nº 43.406.507/0001-52, com sede na Alameda Rio
            Negro, 503, Sala 2020, Alphaville, Barueri/SP, CEP 06454-000, valoriza a privacidade e a proteção dos dados pessoais
            de seus clientes, usuários e demais titulares. Esta Política explica, de forma clara e
            transparente, como tratamos dados pessoais na nossa Plataforma de Gestão de Atendimento
            Omnichannel, em conformidade com a LGPD. Ao utilizar nossa Plataforma ou nos fornecer
            dados, você declara estar ciente desta Política.
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="prose-kentro space-y-10 text-foreground">
          <Block title="1. Definições importantes">
            <ul>
              <li><strong>Dado pessoal:</strong> informação relacionada a pessoa natural identificada ou identificável.</li>
              <li><strong>Titular:</strong> a pessoa natural a quem se referem os dados pessoais.</li>
              <li><strong>Tratamento:</strong> toda operação realizada com dados pessoais (coleta, uso, armazenamento, compartilhamento, eliminação etc.).</li>
              <li><strong>Controlador:</strong> quem toma as decisões sobre o tratamento dos dados.</li>
              <li><strong>Operador:</strong> quem realiza o tratamento em nome do controlador.</li>
              <li><strong>Encarregado (DPO):</strong> pessoa indicada para atuar como canal de comunicação entre a Kentro, os titulares e a Autoridade Nacional de Proteção de Dados (ANPD).</li>
            </ul>
          </Block>

          <Block title="2. Papéis da Kentro no tratamento de dados">
            <p>A Kentro pode atuar de duas formas distintas:</p>
            <ul>
              <li><strong>Como Controladora:</strong> em relação aos dados de seus próprios clientes (contratantes) e dos visitantes do site, por exemplo, dados de cadastro, faturamento e contato comercial.</li>
              <li><strong>Como Operadora:</strong> em relação aos dados que nossos clientes inserem e tratam na Plataforma para gerir seus próprios atendimentos (ex.: dados dos contatos finais atendidos por nossos clientes). Nesses casos, o cliente é o Controlador, e a Kentro trata os dados conforme as instruções dele e o contrato firmado.</li>
            </ul>
          </Block>

          <Block title="3. Como e por que coletamos seus dados">
            <p>
              Coletamos apenas os dados necessários para as finalidades descritas abaixo, sempre
              amparados em uma <strong>base legal</strong> prevista na LGPD.
            </p>
            {/* Desktop: tabela. Mobile: cards. */}
            <div className="hidden md:block -mx-2 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-foreground">
                    <th className="py-2 pr-4 font-semibold">Finalidade</th>
                    <th className="py-2 pr-4 font-semibold">Dados tratados</th>
                    <th className="py-2 font-semibold">Base legal (LGPD)</th>
                  </tr>
                </thead>
                <tbody className="align-top">
                  <Row f="Entrar em contato conforme o interesse manifestado por você no registro" d="Nome, e-mail, telefone/WhatsApp, empresa, segmento" b="Consentimento (art. 7º, I) e/ou diligências pré-contratuais (art. 7º, V)" />
                  <Row f="Cadastro e gestão de usuários na Plataforma" d="Nome completo, e-mail, vínculo com o cliente contratante" b="Execução de contrato (art. 7º, V)" />
                  <Row f="Faturamento e emissão de documentos fiscais" d="Razão social, CNPJ/CPF, endereço, dados de cobrança" b="Cumprimento de obrigação legal (art. 7º, II)" />
                  <Row f="Funcionamento da Plataforma" d="Dados inseridos pelo cliente sobre seus contatos e atendimentos" b="Execução de contrato (na condição de Operadora)" />
                  <Row f="Segurança, prevenção a fraudes e melhoria do serviço" d="Logs de acesso, IP, navegador, dispositivo, data/hora, padrões de uso" b="Legítimo interesse (art. 7º, IX)" />
                  <Row f="Cumprimento de obrigações legais ou ordens judiciais" d="Conforme exigido" b="Obrigação legal / exercício de direitos (art. 7º, II e VI)" />
                </tbody>
              </table>
            </div>
            <div className="grid gap-3 md:hidden">
              {[
                { f: "Entrar em contato conforme o interesse manifestado por você no registro", d: "Nome, e-mail, telefone/WhatsApp, empresa, segmento", b: "Consentimento (art. 7º, I) e/ou diligências pré-contratuais (art. 7º, V)" },
                { f: "Cadastro e gestão de usuários na Plataforma", d: "Nome completo, e-mail, vínculo com o cliente contratante", b: "Execução de contrato (art. 7º, V)" },
                { f: "Faturamento e emissão de documentos fiscais", d: "Razão social, CNPJ/CPF, endereço, dados de cobrança", b: "Cumprimento de obrigação legal (art. 7º, II)" },
                { f: "Funcionamento da Plataforma", d: "Dados inseridos pelo cliente sobre seus contatos e atendimentos", b: "Execução de contrato (na condição de Operadora)" },
                { f: "Segurança, prevenção a fraudes e melhoria do serviço", d: "Logs de acesso, IP, navegador, dispositivo, data/hora, padrões de uso", b: "Legítimo interesse (art. 7º, IX)" },
                { f: "Cumprimento de obrigações legais ou ordens judiciais", d: "Conforme exigido", b: "Obrigação legal / exercício de direitos (art. 7º, II e VI)" },
              ].map((r) => (
                <div key={r.f} className="rounded-lg border border-border bg-card/40 p-4 text-sm">
                  <div className="font-semibold text-foreground">{r.f}</div>
                  <dl className="mt-2 space-y-1.5">
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-muted-foreground/70">Dados tratados</dt>
                      <dd className="text-muted-foreground">{r.d}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-muted-foreground/70">Base legal (LGPD)</dt>
                      <dd className="text-muted-foreground">{r.b}</dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>

            <p>
              <strong>Princípio aplicado:</strong> quando você se registra e manifesta interesse,
              utilizamos seus dados <strong>exclusivamente</strong> para entrar em contato de
              acordo com esse interesse. Não usamos seus dados para finalidades incompatíveis com
              aquilo que motivou o registro.
            </p>
          </Block>

          <Block title="4. Compartilhamento de dados">
            <p>
              A Kentro <strong>não vende</strong> dados pessoais. O compartilhamento ocorre apenas
              quando necessário e nas seguintes hipóteses:
            </p>
            <ul>
              <li><strong>Provedores de infraestrutura e tecnologia (sub-operadores):</strong> data centers e serviços de nuvem de referência contratados no Brasil (como Google, Oracle e Amazon Web Services) e serviço de CDN/segurança Cloudflare, contratados para hospedar e proteger os dados.</li>
              <li><strong>Prestadores de serviços essenciais:</strong> pagamento, emissão fiscal, suporte e comunicação, sempre sob obrigações contratuais de confidencialidade e segurança.</li>
              <li><strong>Autoridades públicas e Poder Judiciário:</strong> para cumprimento de obrigações legais, regulatórias ou ordens judiciais.</li>
              <li><strong>Terceiros indicados por você ou pelo cliente Controlador:</strong> mediante solicitação e consentimento.</li>
              <li><strong>Serviços de terceiros integrados pelo cliente:</strong> quando o cliente conecta serviços externos via nossas APIs e Webhooks (ex.: IA/LLMs), conforme detalhado na seção 5.</li>
            </ul>
            <p>
              Todos os terceiros que tratam dados em nosso nome são obrigados contratualmente a
              manter padrões de segurança compatíveis com esta Política e com a LGPD.
            </p>
          </Block>

          <Block title="5. Integrações de terceiros e uso de IA contratado pelo cliente">
            <p>
              Nossa Plataforma oferece <strong>APIs e Webhooks</strong> que permitem ao cliente
              integrar, por sua própria conta e iniciativa, serviços de terceiros ao sistema (por
              exemplo, serviços de Inteligência Artificial / LLMs, CRMs, ferramentas de automação,
              entre outros). Sobre essas integrações, esclarecemos que:
            </p>
            <ul>
              <li>A <strong>contratação e a configuração</strong> de qualquer serviço de terceiro é de responsabilidade exclusiva do cliente, que atua como Controlador desses dados.</li>
              <li>A Kentro <strong>não contrata, não controla e não se responsabiliza</strong> por serviços de terceiros integrados ao sistema por iniciativa do cliente, nem pelas práticas de privacidade e segurança desses fornecedores.</li>
              <li>Quando o cliente contrata e integra um serviço de <strong>IA/LLM</strong>, apenas os <strong>dados das mensagens</strong> necessários ao funcionamento do recurso são enviados ao provedor escolhido pelo cliente, <strong>conforme os termos do serviço que o próprio cliente contratou</strong>.</li>
              <li>Cabe ao cliente avaliar e aceitar as políticas de privacidade e os termos de uso dos serviços de terceiros que decidir integrar, assegurando-se de que atendem às suas próprias obrigações legais.</li>
            </ul>
          </Block>

          <Block title="6. Localização e armazenamento dos dados">
            <p>
              Os dados tratados na Plataforma são armazenados em{" "}
              <strong>data centers contratados no Brasil</strong>. A Kentro{" "}
              <strong>não realiza transferência internacional dos dados</strong> sob sua
              responsabilidade.
            </p>
            <p>
              A única hipótese em que dados podem ser processados fora dessa infraestrutura ocorre
              quando o <strong>próprio cliente</strong> integra serviços de terceiros (como
              IA/LLMs) que operem em outra localidade — situação descrita na seção 5, sob
              responsabilidade do cliente.
            </p>
          </Block>

          <Block title="7. Como protegemos seus dados">
            <p>
              A segurança dos dados dos nossos clientes é tratada como prioridade absoluta.
              Adotamos medidas técnicas e administrativas robustas, incluindo:
            </p>
            <ul>
              <li><strong>Isolamento de dados:</strong> os dados armazenados de cada cliente são mantidos em servidores/bancos de dados segregados, com acesso restrito.</li>
              <li><strong>Criptografia:</strong> dados criptografados em trânsito (TLS) e em repouso.</li>
              <li><strong>Infraestrutura de referência:</strong> hospedagem em data centers de provedores líderes de mercado (Google, Oracle, Amazon), com CDN e camada de proteção profissional via Cloudflare.</li>
              <li><strong>Controle de acesso:</strong> autenticação de usuários, gestão de permissões e princípio do menor privilégio.</li>
              <li><strong>Monitoramento e firewall:</strong> monitoramento de acessos, registro de logs e barreiras de segurança de rede.</li>
              <li><strong>Backups protegidos:</strong> cópias de segurança criptografadas, igualmente sujeitas às regras de retenção e exclusão.</li>
            </ul>
            <p>
              A segurança também depende de você: utilize senhas fortes, ative recursos de
              autenticação disponíveis e mantenha a confidencialidade das suas credenciais de
              acesso.
            </p>
          </Block>

          <Block title="8. Por quanto tempo retemos seus dados">
            <ul>
              <li><strong>Dados de clientes na Plataforma:</strong> mantidos <strong>enquanto vigorar o contrato</strong>. Ao término do contrato, os dados são <strong>excluídos de forma definitiva</strong>, incluindo todos os backups, <strong>sem possibilidade de recuperação</strong> após o encerramento.</li>
              <li><strong>Dados de contato/registro de interessados:</strong> mantidos pelo tempo necessário ao relacionamento ou até a revogação do consentimento.</li>
              <li><strong>Dados sujeitos a obrigação legal</strong> (ex.: documentos fiscais): mantidos pelos prazos exigidos pela legislação aplicável, mesmo após o fim do contrato, e descartados ao término desses prazos.</li>
            </ul>
          </Block>

          <Block title="9. Seus direitos como titular">
            <p>Conforme a LGPD, você pode, a qualquer tempo e gratuitamente:</p>
            <ul>
              <li>Confirmar a existência de tratamento dos seus dados;</li>
              <li>Acessar seus dados;</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados;</li>
              <li>Solicitar anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;</li>
              <li>Solicitar a portabilidade dos dados;</li>
              <li>Obter informações sobre as entidades com as quais compartilhamos seus dados;</li>
              <li>Revogar o consentimento;</li>
              <li>Opor-se a tratamentos realizados com base no legítimo interesse.</li>
            </ul>
          </Block>

          <Block title="10. Como exercer seus direitos">
            <p>
              Você pode exercer seus direitos a qualquer momento entrando em contato com nosso
              Encarregado pela Proteção de Dados (DPO):
            </p>
            <ul>
              <li><strong>E-mail:</strong>{" "}
                <a className="text-primary-glow hover:underline" href={`mailto:${KENTRO.email}`}>
                  {KENTRO.email}
                </a>
              </li>
              <li><strong>Encarregado (DPO):</strong>{" "}
                <a className="text-primary-glow hover:underline" href="mailto:dpo@kentro.com.br">
                  dpo@kentro.com.br
                </a>
              </li>
            </ul>
            <p>
              Para sua segurança, poderemos solicitar comprovação de identidade ou da condição de
              representante legal antes de atender à solicitação, a fim de evitar fraudes.
            </p>
            <p>
              Caso o tratamento tenha sido realizado pela Kentro na condição de{" "}
              <strong>Operadora</strong>, encaminharemos sua solicitação ao cliente Controlador
              responsável ou o orientaremos sobre como prosseguir.
            </p>
          </Block>

          <Block title="11. Cookies e tecnologias de rastreamento">
            <p>
              Nosso site pode utilizar cookies e tecnologias semelhantes para garantir o
              funcionamento da página, analisar o uso e melhorar a experiência.
            </p>
          </Block>

          <Block title="12. Links para sites de terceiros">
            <p>
              Nossa Plataforma e nosso site podem conter links para páginas de terceiros. Não nos
              responsabilizamos pelo conteúdo nem pelas práticas de privacidade desses sites.
              Recomendamos a leitura das respectivas políticas.
            </p>
          </Block>

          <Block title="13. Alterações nesta Política">
            <p>
              Esta Política pode ser atualizada periodicamente para refletir mudanças legais,
              regulatórias ou em nossos serviços. As alterações serão publicadas nesta página, com
              atualização da data no topo, e, quando relevantes, comunicadas por e-mail ou aviso
              na Plataforma.
            </p>
          </Block>

          <Block title="14. Contato">
            <p>Em caso de dúvidas sobre esta Política ou sobre o tratamento dos seus dados:</p>
            <ul>
              <li><strong>E-mail:</strong>{" "}
                <a className="text-primary-glow hover:underline" href={`mailto:${KENTRO.email}`}>
                  {KENTRO.email}
                </a>
              </li>
              <li><strong>Telefone:</strong> <a href={KENTRO.whatsappComercialUrl} target="_blank" rel="noopener noreferrer" className="text-primary-glow hover:underline">0800 550 2020</a></li>
              <li><strong>Endereço:</strong> Alameda Rio Negro, 503, Sala 2020, Alphaville, Barueri/SP, CEP 06454-000</li>
            </ul>
          </Block>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <h2 className="text-lg font-semibold">Documentos relacionados</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Veja também o Aviso de Privacidade completo (LGPD) e nosso Canal de Denúncias.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/aviso-de-privacidade"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <ShieldCheck className="size-4" /> Aviso de Privacidade (LGPD)
            </Link>
            <Link
              to="/ouvidoria"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <Mail className="size-4" /> Canal de Denúncias
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-muted-foreground [&_a]:text-primary-glow [&_li]:my-1 [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}

function Row({ f, d, b }: { f: string; d: string; b: string }) {
  return (
    <tr className="border-b border-border/60">
      <td className="py-3 pr-4 text-foreground">{f}</td>
      <td className="py-3 pr-4">{d}</td>
      <td className="py-3">{b}</td>
    </tr>
  );
}
